import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DataType } from '@mode-switch/express';

import { FieldFormatHelpers } from '@mode/charts/util-vegas';
import { FeatureFlag, FlamingoTypes } from '@mode/shared/contract-common';
import { getInitialFlagValue } from '@mode/shared/util-js';
import { CompiledAttr } from '@mode/shared/util-switch';
import { startOfToday } from 'date-fns';

/* eslint-disable @angular-eslint/component-selector */
enum SwitchAttrProp {
  Name = 'name',
  FieldType = 'fieldType',
  DataType = 'dataType',
  Description = 'description',
  Aggregation = 'aggregation',
  Formatting = 'formatting',
  Spacer = 'spacer', // Just a way to declare an empty mat table column for presentation purpose
}

interface updateSwitchAttrProps {
  attr: FlamingoTypes.ModeCalc;
  previousAttr: FlamingoTypes.ModeCalc;
  queryToken: string;
}

const FORMATTING_EXAMPLE_TEXT = 'Example text';

@Component({
  selector: 'mode-switch-view-fields',
  templateUrl: './switch-view-fields.component.html',
  styleUrls: ['./switch-view-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwitchViewFieldsComponent implements OnChanges, OnInit {
  @Input() fieldFilter = '';
  @Input() switchView: FlamingoTypes.ModeView | null = null;
  @Input() totalRecords = 0;
  @Input() size = '';
  @Input() isImportedDataset = false;
  @Input() disableCalcFieldsManagement = false;
  @Input() showFieldDescriptionColumn = false;
  @Input() fieldDescReadOnly = false;

  @Output() editCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() viewCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() removeCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() duplicateCalcField = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() createSwitchAttr = new EventEmitter<FlamingoTypes.ModeCalc>();
  @Output() updateSwitchAttr = new EventEmitter<updateSwitchAttrProps>();
  @Output() fieldDescEditToggled = new EventEmitter<boolean>();

  totalFields = 0;
  totalCalcFields = 0;
  switchViewAttrs: CompiledAttr[] = [];
  hasDefaultFormat = false;
  datasetFieldSettingsFormattingColumnEnabled =
    getInitialFlagValue(FeatureFlag.DatasetFieldSettingsFormattingColumnEnabled) || false;

  private columnNames: string[] = [];
  private parentCalcNames: string[] = [];

  localNameErrorHTML = `This field name conflicts with a column<br/>
                            or field in the source Dataset. Edit the<br/>
                            local field name to resolve the conflict.`;

  genericErrorHTML = `The formula used in this field is no<br/>
                         longer valid. Edit the existing formula<br/>
                         to see what’s wrong, or delete the field.`;
  exampleFormatTooltip = `Formatting examples do not apply to quick chart tables.`;

  // This array determines what mat-table columns and in what order get displayed
  displayedColumns: string[] = [];

  ngOnInit(): void {
    this.setColumns();
    this.setSwitchViewAttrs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['switchView']) {
      this.setSwitchViewAttrs();
    }
    if (changes['fieldFilter']) {
      const formattedFieldFilter = changes['fieldFilter'].currentValue.replace(/[^A-Z0-9]/gi, '').toLowerCase();
      this.filterSwitchViewAttrs(formattedFieldFilter);
    }
  }

  setColumns(): void {
    if (this.showFieldDescriptionColumn) {
      this.displayedColumns = [
        SwitchAttrProp.Name,
        SwitchAttrProp.Description,
        SwitchAttrProp.DataType,
        SwitchAttrProp.Aggregation,
      ];
      if (this.datasetFieldSettingsFormattingColumnEnabled) {
        this.displayedColumns.splice(3, 0, SwitchAttrProp.Formatting);
      }
    } else {
      this.displayedColumns = [
        SwitchAttrProp.Name,
        SwitchAttrProp.Spacer,
        SwitchAttrProp.FieldType,
        SwitchAttrProp.DataType,
        SwitchAttrProp.Aggregation,
      ];
      if (this.datasetFieldSettingsFormattingColumnEnabled) {
        this.displayedColumns.splice(4, 0, SwitchAttrProp.Formatting);
      }
    }
  }

  setSwitchViewAttrs(): void {
    // Sort attrs by name
    this.switchViewAttrs = this.switchView?.attrs
      ? this.switchView.attrs
          .map((attr) => CompiledAttr.fromAttr(<FlamingoTypes.ModeView>this.switchView, attr))
          .sort((a, b) => (a.attrName ? a.attrName.localeCompare(<string>b.attrName) : 0))
      : [];
    this.hasDefaultFormat = this.switchViewAttrs.some((attr) => attr.defaultFormat !== undefined);
    this.totalFields = this.switchViewAttrs.length;
    this.totalCalcFields = this.switchViewAttrs.filter((attr) => attr.isCalc).length;
    this.parentCalcNames = <string[]>(
      this.switchViewAttrs.filter((attr) => !attr.isLocal).map((attr: CompiledAttr) => attr.attrName)
    );
  }

  getUrlDisplayLink(attr: CompiledAttr) {
    if (attr?.defaultFormat?.linkDisplayText) {
      return attr.defaultFormat.linkDisplayText;
    }
    return FORMATTING_EXAMPLE_TEXT;
  }

  getExampleFormatting(attr: CompiledAttr) {
    const field = attr.withNewFormula(attr.formula);
    switch (field.formulaDataType) {
      case DataType.Name.Decimal:
      case DataType.Name.Float:
      case DataType.Name.Integer:
        return FieldFormatHelpers.formatValue(10000000.034, field);
      case DataType.Name.DateTime:
      case DataType.Name.Timestamp:
        return FieldFormatHelpers.formatValue(startOfToday(), field);
      case DataType.Name.Boolean:
        return FieldFormatHelpers.formatValue(true, field);
      case DataType.Name.String:
        return FieldFormatHelpers.formatValue(FORMATTING_EXAMPLE_TEXT, field);
      default:
        return '';
    }
  }

  getFormulaTypeIcon(attr: CompiledAttr): string {
    const calcPrefix = attr.isCalc ? '-calc' : '';

    if (!attr.formulaDataType) {
      return 'icon-field-calc-broken';
    } else {
      switch (attr.formulaDataType) {
        case DataType.Name.Decimal:
        case DataType.Name.Float:
        case DataType.Name.Integer:
          return `icon-field${calcPrefix}-numeric`;
        case DataType.Name.DateTime:
        case DataType.Name.Timestamp:
          return `icon-field${calcPrefix}-date`;
        case DataType.Name.Boolean:
          return `icon-field${calcPrefix}-boolean`;
        case DataType.Name.String:
          return `icon-field${calcPrefix}-string`;
      }
    }
    return '';
  }

  hasConflict(attr: CompiledAttr): boolean | undefined {
    return this.isImportedDataset && attr.isLocal && this.localCalcDuplicatesColumnName(attr);
  }

  getVarType(attr: CompiledAttr): string {
    return attr.isDiscrete ? 'discrete' : 'continuous';
  }

  viewCalc(calcAttr: CompiledAttr): void {
    this.viewCalcField.emit(calcAttr.attr as FlamingoTypes.ModeCalc);
  }

  editCalc(calcAttr: CompiledAttr): void {
    this.editCalcField.emit(calcAttr.attr as FlamingoTypes.ModeCalc);
  }

  duplicateCalc(calcAttr: CompiledAttr): void {
    this.duplicateCalcField.emit(calcAttr.attr as FlamingoTypes.ModeCalc);
  }

  removeCalc(calcAttr: CompiledAttr): void {
    this.removeCalcField.emit(calcAttr.attr as FlamingoTypes.ModeCalc);
  }

  localCalcDuplicatesColumnName(attr: CompiledAttr): boolean | undefined {
    return attr.isCalc && attr.isLocal && (this.matchesParentCalcName(attr) || this.matchesColumnName(attr));
  }

  matchesColumnName(attr: CompiledAttr): boolean {
    return !!attr.attrName && this.columnNames.includes(attr.attrName);
  }

  matchesParentCalcName(attr: CompiledAttr): boolean {
    return !!attr.attrName && this.parentCalcNames.includes(attr.attrName);
  }

  filterSwitchViewAttrs(fieldFilter: string): void {
    if (fieldFilter) {
      this.setSwitchViewAttrs();
      this.switchViewAttrs = this.switchViewAttrs.filter((switchViewAttr) =>
        switchViewAttr.attr.name
          .replace(/[^A-Z0-9]/gi, '')
          .toLowerCase()
          .includes(fieldFilter)
      );
    } else {
      this.setSwitchViewAttrs();
    }
  }

  setAttributeDescription(attr: any, userInput: string): FlamingoTypes.ModeCalc {
    attr.desc = userInput;
    return attr;
  }

  updateFieldDesc(attr: FlamingoTypes.ModeCalc, userInput: string): void {
    const previousAttr = attr;
    const queryToken = <string>this.switchView?.name;
    const userInputUnderCharacterLimit = userInput.length <= 350;
    if (attr.desc === undefined && userInputUnderCharacterLimit) {
      this.setAttributeDescription(attr, userInput);
      this.createSwitchAttr.emit(attr);
    } else if (previousAttr.desc !== userInput && userInputUnderCharacterLimit) {
      this.setAttributeDescription(attr, userInput);
      this.updateSwitchAttr.emit({ attr, previousAttr, queryToken });
    }
  }
}
